import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Button,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  IconButton,
  FormLabel,
  Avatar,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import PrintIcon from "@mui/icons-material/Print";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { AttachMoney } from "@mui/icons-material";
import AppContext from "../AppContext";
import { db } from '../firebase'; 
import { collection, getDocs,doc,getDoc} from 'firebase/firestore';

const OrderDialog = ({
  confirmationOpen,
  handleConfirmationClose,
  confirmOrderId,
  selectedOrder,
  orderCompleted,
  balance,
  formatToMyanmarCurrency,
  splitPayments,
  handleSplitPaymentMethodChange,
  handleSplitPaymentAmountChange,
  addSplitPayment,
  removeSplitPayment,
  balanceError,
  handleCollectBalance,
  isBalanceCollected,
  collectBalanceLoading,
  handlePrint,
  deliveryFee,
  deliveryPaymentMethod,
  setDeliveryPaymentMethod,
  handlePayDelivery,
  isDeliveryPaid,
  payDeliveryLoading,
  handleCompleteOrder,
  completeOrderLoading,
  selectedCashier,
  setSelectedCashier,
}) => {
  const { shopId, t } = useContext(AppContext);
  const [cashiers, setCashiers] = useState([]);

  useEffect(() => {
    const fetchCashiers = async () => {
      const shopRef = doc(db, "shops", shopId);
      const shopSnap = await getDoc(shopRef);
      if (shopSnap.exists()) {
        const cashiers = shopSnap.data().cashiers || [];
        setCashiers(cashiers);
      }
    };
    fetchCashiers();
  }, [shopId]);

  return (
    <Dialog
      open={confirmationOpen}
      onClose={handleConfirmationClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        <Typography variant="h4">
          {t("Order")} #{selectedOrder ? selectedOrder.orderID : t("Loading...")}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {orderCompleted ? (
          <Typography variant="h5" color="error">
            {t("This Order is Already Completed")}
          </Typography>
        ) : (
          <>

            <Typography variant="h5" sx={{ mb: 2 }} gutterBottom>
              {t("Outstanding Balance")}: {formatToMyanmarCurrency(balance)} {t("MMK")}
            </Typography>

            {/* Split Payments UI */}
            {splitPayments.map((payment, index) => (
              <Box
                key={index}
                sx={{ display: "flex", alignItems: "center", mb: 2 }}
              >
                <FormControl sx={{ minWidth: 180, mr: 2 }}>
                  <InputLabel>{t("Payment Method")}</InputLabel>
                  <Select
                    value={payment.method}
                    label={t("Payment Method")}
                    onChange={(e) =>
                      handleSplitPaymentMethodChange(index, e.target.value)
                    }
                    disabled={balance === 0}
                  >
                    <MenuItem value="Cash">{t("Cash")}</MenuItem>
                    <MenuItem value="Kpay">{t("Kpay")}</MenuItem>
                    <MenuItem value="Wave Pay">{t("Wave Pay")}</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  type="number"
                  label={t("Payment Amount")}
                  value={payment.amount}
                  onChange={(e) =>
                    handleSplitPaymentAmountChange(index, e.target.value)
                  }
                  inputProps={{ max: balance }}
                  sx={{ mr: 2, flex: 1 }}
                  disabled={balance === 0}
                />
                {splitPayments.length > 1 && (
                  <IconButton onClick={() => removeSplitPayment(index)}>
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            ))}

            {splitPayments.length < 3 && (
              <Button
                onClick={addSplitPayment}
                variant="outlined"
                startIcon={<AddIcon />}
                sx={{ mb: 2 }}
                disabled={balance === 0}
              >
                {t("Add Payment Method")}
              </Button>
            )}
            <Box>
              {balanceError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {t("Please ensure split payments total matches the balance.")}
                </Alert>
              )}
              <Button
                onClick={handleCollectBalance}
                color="success"
                variant="contained"
                startIcon={<AttachMoney />}
                disabled={
                  isBalanceCollected ||
                  balance === 0 ||
                  orderCompleted ||
                  collectBalanceLoading
                }
                disableElevation
              >
                {collectBalanceLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t("Collect Balance")
                )}
              </Button>
              <Button
                onClick={handlePrint}
                color="primary"
                variant="outlined"
                startIcon={<PrintIcon />}
                disabled={!isBalanceCollected || orderCompleted}
                disableElevation
                sx={{ ml: 1 }}
              >
                {t("Print Receipt")}
              </Button>
            </Box>

            <Typography variant="h5" gutterBottom mt={2}>
              {t("Delivery Charge")}: {formatToMyanmarCurrency(deliveryFee)} {t("MMK")}
            </Typography>
            <FormControl component="fieldset">
              <FormLabel component="legend">{t("Delivery Payment Method")}</FormLabel>
              <RadioGroup
                row
                aria-label={t("Payment method")}
                name="payment-method"
                value={deliveryPaymentMethod}
                onChange={(e) => setDeliveryPaymentMethod(e.target.value)}
              >
                <FormControlLabel
                  value="Cash"
                  control={<Radio />}
                  label={t("Cash")}
                />
              </RadioGroup>
            </FormControl>
            <Box>
              <Button
                onClick={handlePayDelivery}
                color="info"
                variant="contained"
                startIcon={<LocalShippingIcon />}
                disabled={
                  isDeliveryPaid ||
                  deliveryFee === 0 ||
                  orderCompleted ||
                  payDeliveryLoading
                }
                disableElevation
              >
                {payDeliveryLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t("Pay Delivery")
                )}
              </Button>
            </Box>
             {/* Cashier Selection */}
             <Typography variant="h6" mt={2} gutterBottom>
              {t("Select Cashier")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                overflowX: "auto",
                gap: 2,
                py: 2,
                "&::-webkit-scrollbar": {
                  height: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,.2)",
                  borderRadius: "4px",
                },
              }}
            >
              {cashiers.map((name) => (
                <Box
                  key={name}
                  onClick={() => setSelectedCashier(name)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    minWidth: 100,
                    cursor: "pointer",
                    p: 1,
                    borderRadius: 2,
                    backgroundColor:
                      selectedCashier === name ? "grey.100" : "transparent",
                    "&:hover": {
                      backgroundColor: "grey.100",
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      mb: 1,
                      fontSize: "1.5rem",
                      backgroundColor:
                        selectedCashier === name ? "primary.main" : "grey.500",
                    }}
                    alt={name}
                  >
                    {name.charAt(0)}
                  </Avatar>
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: "center",
                      fontWeight:
                        selectedCashier === name ? "bold" : "normal",
                    }}
                  >
                    {name}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCompleteOrder}
          color="primary"
          variant="contained"
          startIcon={<TaskAltIcon />}
          disabled={
            orderCompleted ||
            completeOrderLoading ||
            (!isBalanceCollected && balance !== 0) ||
            (!isDeliveryPaid && deliveryFee !== 0) ||
            !selectedCashier // Disable if no cashier is selected
          }
          disableElevation
        >
          {completeOrderLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("Complete Order")
          )}
        </Button>
        <Button
          onClick={handleConfirmationClose}
          variant="outlined"
          disableElevation
        >
          {t("Close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderDialog;
